/**
 * Login-style validation dialog for apply-now / invite registration only.
 * Matches `showLoginValidationMessage` (shield header, pink banner, teal Ok).
 * Do not use from other modules — use shared ValidationModal instead.
 */

import { X, ShieldAlert } from 'lucide-react'
import { createPortal } from 'react-dom'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { NOTIFICATION_TITLES } from '@/constants/notifications'
import styles from '@/features/auth/styles/loginValidation.module.css'
import type { LoginStyleValidationModalProps } from '../types'

function messageLooksLikeHtml(message: string): boolean {
  return /<[a-z][\s\S]*>/i.test(message.trim())
}

function formatValidationMessage(message: string): string {
  const trimmed = message.trim() || 'Please correct the highlighted fields.'
  return trimmed.replace(/([.!?])\s+(?=[A-Z])/g, '$1\n')
}

export function LoginStyleValidationModal({
  isOpen,
  title,
  message,
  onClose,
  onConfirm,
  confirmText = 'Ok',
}: LoginStyleValidationModalProps) {
  useBodyScrollLock(isOpen)

  if (!isOpen) return null

  const handleConfirm = () => {
    onConfirm?.()
    onClose()
  }

  const modalTitle = title || NOTIFICATION_TITLES.missingFields
  const raw = message.trim() || 'Please correct the highlighted fields.'
  const isHtml = messageLooksLikeHtml(raw)
  const displayText = isHtml ? raw : formatValidationMessage(raw)

  const content = (
    <div
      className={styles.overlay}
      style={{ zIndex: 2147483640 }}
      onClick={onClose}
      role="presentation"
    >
      <div
        className={styles.panel}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="login-style-validation-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <span className={styles.headerIcon} aria-hidden>
            <ShieldAlert className="h-5 w-5" strokeWidth={2} />
          </span>
          <h3 id="login-style-validation-title" className={styles.title}>
            {modalTitle}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className={styles.closeButton}
            aria-label="Close"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.alertBanner}>
            <span className={styles.alertIcon} aria-hidden>
              !
            </span>
            {isHtml ? (
              <div
                className={`${styles.message} validation-message-html`}
                dangerouslySetInnerHTML={{ __html: displayText }}
              />
            ) : (
              <div className={styles.message}>{displayText}</div>
            )}
          </div>
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            onClick={handleConfirm}
            className={styles.okButton}
            autoFocus
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}
